import log from 'loglevel';

/**
 * Check if service worker needs update
 */
const checkAndUpdateServiceWorker = async (registration: ServiceWorkerRegistration): Promise<void> => {
    try {
        // Check if service worker update is available
        await registration.update();
        
        // If there's a waiting service worker, it means there's an update
        if (registration.waiting) {
            // Send message to skip waiting
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        // Listen for new service workers
        registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (!newWorker) return;

            newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    // New service worker available
                    newWorker.postMessage({ type: 'SKIP_WAITING' });
                }
            });
        });

        // When the service worker is updated, reload the page
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            window.location.reload();
        });
    } catch (error) {
        console.error('Error checking for Service Worker updates:', error);
    }
};

/**
 * Request permission and get subscription for push notifications
 */
export const requestNotificationPermission = async (): Promise<string | null> => {
    try {

        // First check if the browser supports notifications
        if (!('Notification' in window)) {
            log.warn('This browser does not support notifications');
            return null;
        }

        // Request permission first
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
            log.warn('Notification permission denied');
            return null;
        }

        // Check for service worker support
        if (!('serviceWorker' in navigator)) {
            log.warn('Service Workers not supported');
            return null;
        }

        // Check for Push API support
        if (!('PushManager' in window)) {
            log.warn('Push API not supported');
            return null;
        }

        try {
            // First try to get existing registration
            let registration = await navigator.serviceWorker.getRegistration('/push-sw.js');
            
            if (!registration) {
                // Only register new if none exists
                registration = await navigator.serviceWorker.register('/push-sw.js', {
                    updateViaCache: 'none' // Disable SW script caching
                });
            } else {
                await checkAndUpdateServiceWorker(registration);
            }

            // Wait for the service worker to be ready
            await navigator.serviceWorker.ready;

            // Get existing subscription or create new one
            let subscription = await registration.pushManager.getSubscription();
            
            // Check if subscription exists and if the key matches current VAPID key
            const currentVapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
            if (subscription) {
                const options = subscription.options;
                const existingKey = options.applicationServerKey;
                
                if (existingKey) {
                    // Convert ArrayBuffer to URL-safe base64 string
                    const existingKeyArray = new Uint8Array(existingKey);
                    const existingKeyBase64 = btoa(String.fromCharCode.apply(null, Array.from(existingKeyArray)))
                        .replace(/\+/g, '-')
                        .replace(/\//g, '_')
                        .replace(/=+$/, '');
                    
                    // If keys don't match, unsubscribe and create new subscription
                    if (existingKeyBase64 !== currentVapidKey) {
                        await subscription.unsubscribe();
                        subscription = null;
                    }
                } else {
                    // If no key exists, unsubscribe to be safe
                    await subscription.unsubscribe();
                    subscription = null;
                }
            }

            if (!subscription) {
                // Create new subscription
                subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: import.meta.env.VITE_VAPID_PUBLIC_KEY
                });
            }

            // Convert subscription to string for storage/transmission
            const subscriptionString = JSON.stringify(subscription);

            return subscriptionString;
        } catch (error) {
            const err = error as Error;
            log.error('Error during push subscription:', err);
            return null;
        }
    } catch (error) {
        const err = error as Error;
        log.error('An error occurred while setting up push notifications:', err);
        return null;
    }
};

// Service worker message handler
if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
        const payload = event.data;
        if (payload.type === 'PUSH_NOTIFICATION') {
            // Handle push notification data
            console.log('Received push notification:', payload.data);
        }
    });
}